import { getModularBenefitById } from '@ben/columbia/api/modular-benefit-detail';
import EligibilityRules from '@ben/columbia/components/benefit-config/eligibility-rules/eligibility-rules';
import type { StepsFields } from '@ben/columbia/components/benefit-config/elements/types';
import EnrolmentSettings from '@ben/columbia/components/benefit-config/enrolment-settings/enrolment-settings';
import EnrolmentWindows from '@ben/columbia/components/benefit-config/enrolment-windows/enrolment-windows';
import EnrollmentConfig from '@ben/columbia/components/benefit-config/enrollment-config/enrollment-config';
import GeneralInformation from '@ben/columbia/components/benefit-config/general-information/general-information';
import LifeEventsSection from '@ben/columbia/components/benefit-config/life-events/life-events-section';
import Prices from '@ben/columbia/components/benefit-config/prices/prices';
import RateTable from '@ben/columbia/components/benefit-config/rate-table/rate-table';
import { deepMergeArraysAndObjects } from '@ben/columbia/components/benefit-config/utils';
import { UnstyledFieldset } from '@ben/orion/fieldset';
import Loader from '@ben/orion/loader';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import CustomComponent from '@ben/shared/components/benefit-sections/custom-component';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import type { ConfigurationDomain } from './domains';
import { domainLabel, LIFE_EVENTS } from './domains';
import { Muted, Stack } from './shared';

/**
 * Shows a proposed payload through Columbia's own benefit sections, so an IM
 * reviews a change in the screens they configure benefits in every day.
 *
 * The sections read their values from react-hook-form context, which is the
 * seam this uses: a throwaway local form is seeded from the payload and the
 * real components render against it. Nothing is wired to a save, so the form
 * exists only to feed the display.
 */

type PayloadRecord = Record<string, unknown>;

/**
 * The payload keys each domain owns, from the backend's DOMAIN_ADAPTERS
 * serializer paths. Keys arrive camelCased because the shared axios instance
 * converts responses.
 *
 * Used only to place the payload where a section reads it. Which domain a
 * change belongs to is not inferred from these — the API states it — so a key
 * missing here leaves a field blank rather than sending the whole change to
 * the wrong section.
 */
const DOMAIN_KEYS = {
  selections: ['steps'],
  pricing: ['subPrices', 'rateTables'],
  eligibility: ['eligibilityRules'],
  windows: ['benefitWindows'],
  [LIFE_EVENTS]: ['lifeEventPolicies'],
  enrolment: [
    'enrollmentConfig',
    'activationDelay',
    'deactivationDelay',
    'changeDelay',
    'fulfillmentDelay',
  ],
  details: [
    'newBenefitType',
    'enrolmentType',
    'countries',
    'product',
    'productType',
    'policyNumberType',
    'managedBy',
    'isAuditable',
  ],
} as const;

/**
 * Details and content carry one key per locale (nameEn, contentEsXl, …), so
 * they are matched on prefix rather than listed.
 */
const DETAILS_LOCALES = /^(name|displayName|shortDescription)[A-Z]/;
const CONTENT_LOCALES = /^content[A-Z]/;

const NO_MESSAGES = {};
const NO_ERRORS = {};

const asRecords = (payload: PayloadRecord, key: string): PayloadRecord[] => {
  const value = payload[key];
  return Array.isArray(value)
    ? value.filter(
        (item): item is PayloadRecord =>
          Boolean(item) && typeof item === 'object'
      )
    : [];
};

/** Components declared directly, or nested inside the payload's steps. */
const asComponents = (payload: PayloadRecord): PayloadRecord[] => {
  const direct = asRecords(payload, 'components');
  if (direct.length > 0) return direct;

  return asRecords(payload, 'steps').flatMap((step) =>
    asRecords(step, 'components')
  );
};

/** Payload ids and keys are unknown-typed, so narrow before using as a key. */
const componentKey = (component: PayloadRecord, index: number): string => {
  const candidate = component.id ?? component.key;
  return typeof candidate === 'string' || typeof candidate === 'number'
    ? String(candidate)
    : String(index);
};

const pick = (payload: PayloadRecord, keys: readonly string[]): PayloadRecord =>
  Object.fromEntries(
    keys
      .filter((key) => payload[key] !== undefined)
      .map((key) => [key, payload[key]])
  );

const pickMatching = (payload: PayloadRecord, pattern: RegExp): PayloadRecord =>
  Object.fromEntries(
    Object.entries(payload).filter(([key]) => pattern.test(key))
  );

/**
 * One section, driven by a form seeded with the live benefit and the proposal
 * merged over it.
 *
 * The merge matters: a change carries one domain's slice, so rendered from the
 * payload alone every other field in the section would be blank and read as
 * though the change had cleared it. `overlay` places the payload's keys where
 * the section reads them, which for pricing and selections is inside
 * additionalConfig.
 *
 * `title` is only for the sections Columbia does not head itself.
 */
const Section = ({
  title,
  benefit,
  overlay,
  children,
}: {
  title?: string;
  benefit: IModularBenefitDetail;
  overlay: PayloadRecord;
  children: ReactNode;
}) => {
  const merged = useMemo(
    () => deepMergeArraysAndObjects(benefit, overlay) as IModularBenefitDetail,
    [benefit, overlay]
  );
  /**
   * `values`, not `defaultValues`, and that is load-bearing rather than a
   * style choice.
   *
   * The sections normalise as they mount: three effects in Prices and three in
   * EnrolmentWindows call `setValue` to clear fields their own rules say
   * cannot apply alongside what is set. Correct while someone is editing, and
   * wrong here — it would show a reviewer a field the proposal never touched,
   * on a screen whose whole job is to say what a change does. `values` keeps
   * re-applying the payload, so those writes do not survive; with
   * `defaultValues` they would. Pinned by a test.
   */
  const form = useForm<IModularBenefitDetail>({ values: merged });

  return (
    <section>
      {title ? (
        <Typography variant={TypographyVariant.h3}>{title}</Typography>
      ) : null}
      <FormProvider {...form}>
        <Inert disabled>{children}</Inert>
      </FormProvider>
    </section>
  );
};

/**
 * Life events reach into a second form for the steps its restrictions point
 * at. Those steps are not part of a life-events payload, so this one is seeded
 * from the benefit alone.
 */
const LifeEvents = ({ benefit }: { benefit: IModularBenefitDetail }) => {
  const stepsForm = useForm<StepsFields>({
    values: {
      additionalConfig: { steps: benefit.additionalConfig?.steps ?? [] },
    },
  });

  return (
    <LifeEventsSection
      stepsForm={stepsForm}
      errors={NO_MESSAGES}
      successes={NO_MESSAGES}
    />
  );
};

interface DomainSection {
  /** Where this domain's payload keys sit for the section that reads them. */
  overlay: (payload: PayloadRecord) => PayloadRecord;
  /** Only for the sections Columbia does not head itself. */
  title?: string;
  render: (args: {
    benefit: IModularBenefitDetail;
    payload: PayloadRecord;
  }) => ReactNode;
}

const GeneralInformationSection = () => (
  <GeneralInformation
    errors={NO_ERRORS}
    warnings={NO_MESSAGES}
    successes={NO_MESSAGES}
  />
);

/**
 * The Columbia section each configuration domain is reviewed in.
 *
 * Keyed by the domain the API reports on the change, so nothing here decides
 * which section a payload belongs to. Details and content are both edited in
 * general information, and the five change domains with no revisioned payload
 * slice have no entry — a change in one of those has nothing to render.
 */
const DOMAIN_SECTIONS = {
  details: {
    overlay: (payload) => ({
      ...pick(payload, DOMAIN_KEYS.details),
      ...pickMatching(payload, DETAILS_LOCALES),
    }),
    render: GeneralInformationSection,
  },
  content: {
    overlay: (payload) => pickMatching(payload, CONTENT_LOCALES),
    render: GeneralInformationSection,
  },
  enrolment: {
    overlay: (payload) => pick(payload, DOMAIN_KEYS.enrolment),
    render: () => (
      <>
        <EnrolmentSettings warnings={NO_MESSAGES} successes={NO_MESSAGES} />
        <EnrollmentConfig />
      </>
    ),
  },
  eligibility: {
    overlay: (payload) => pick(payload, DOMAIN_KEYS.eligibility),
    render: () => <EligibilityRules />,
  },
  pricing: {
    overlay: (payload) => ({
      additionalConfig: pick(payload, DOMAIN_KEYS.pricing),
    }),
    render: () => (
      <>
        <Prices
          errors={NO_ERRORS}
          warnings={NO_MESSAGES}
          successes={NO_MESSAGES}
        />
        <RateTable />
      </>
    ),
  },
  windows: {
    overlay: (payload) => pick(payload, DOMAIN_KEYS.windows),
    render: () => (
      <EnrolmentWindows errors={NO_MESSAGES} successes={NO_MESSAGES} />
    ),
  },
  [LIFE_EVENTS]: {
    overlay: (payload) => pick(payload, DOMAIN_KEYS[LIFE_EVENTS]),
    render: ({ benefit }) => <LifeEvents benefit={benefit} />,
  },
  selections: {
    title: 'Selections',
    overlay: (payload) => ({
      additionalConfig: pick(payload, DOMAIN_KEYS.selections),
    }),
    render: ({ payload }) => (
      <Stack $gap={3}>
        {asComponents(payload).map((component, index) => (
          <CustomComponent
            key={componentKey(component, index)}
            {...component}
            disabled
            onChange={() => undefined}
          />
        ))}
      </Stack>
    ),
  },
} satisfies Record<ConfigurationDomain, DomainSection>;

const sectionFor = (domain: string): DomainSection | undefined =>
  domain in DOMAIN_SECTIONS
    ? DOMAIN_SECTIONS[domain as ConfigurationDomain]
    : undefined;

export const PayloadPreview = ({
  domain,
  payload,
  benefitId,
}: {
  domain: string;
  payload: Record<string, unknown>;
  benefitId: number | undefined;
}) => {
  const section = sectionFor(domain);

  const benefitQuery = useQuery({
    queryKey: ['modularBenefitForPreview', benefitId],
    queryFn: () => getModularBenefitById(benefitId),
    enabled: section !== undefined && benefitId !== undefined,
  });

  if (!section) {
    return (
      <Muted>
        {domainLabel(domain)} has no Columbia section to preview. The
        differences above and the full payload below describe the change.
      </Muted>
    );
  }

  if (benefitId === undefined) {
    return (
      <Muted>
        This change proposes a benefit that does not exist yet, so there is
        nothing to show it against. The full payload below describes it.
      </Muted>
    );
  }

  if (benefitQuery.isLoading) return <Loader />;

  const benefit = benefitQuery.data;
  if (!benefit) {
    return (
      <Muted>
        The benefit could not be loaded, so this change cannot be shown in
        context. The differences above and the full payload below describe it.
      </Muted>
    );
  }

  return (
    <Stack $gap={4}>
      <Section
        title={section.title}
        benefit={benefit}
        overlay={section.overlay(payload)}
      >
        {section.render({ benefit, payload })}
      </Section>
    </Stack>
  );
};

/**
 * A disabled fieldset natively disables the form controls inside it, which
 * makes the real sections read-only without changing them. pointer-events
 * covers the few controls that are not native inputs. What those sections
 * write to the form as they mount is handled by `Section`'s form options, not
 * here — a fieldset has no say over effects.
 */
const Inert = styled(UnstyledFieldset)`
  ${({ theme }) => css`
    border: 0;
    padding: 0;
    margin: ${theme.spacing(1)} 0 0;
    min-width: 0;
    pointer-events: none;
    display: grid;
    gap: ${theme.spacing(3)};
  `}
`;
